import { toast } from 'react-toastify';
import {
  all,
  call,
  fork,
  put,
  take,
  takeLatest,
  select,
} from 'redux-saga/effects';
import {
  setCurrentUser,
  postUserDataForOTVSuccess,
  postUserDataForOTVFailure,
  resetStoreDataStart,
} from './user.actions';
import {
  setSignupWizardActivePageName,
  setAsUnregisteredUser,
  setWidgetCode,
  setVerificationCodeTimerStatus,
  setWidgetToken,
  // setUserOtpToken,
} from '../app/app.actions';
import {
  isEmailExists,
  isMobileExists,
  saveAsUnregisteredUser,
  handleValidateSmsVerificationCode,
  handleLoginUser,
  handleLogoutUser,
  handleResetPassword,
  handleSignup,
  handleSendVerificationSms,
  handleUserVerifyWithOTV,
  handleUserLoginWithOTV,
} from './user.helpers';
import userTypes from './user.types';
import { INITIAL_STATE } from './user.reducer';
import { createUserObject } from './user.adapter';
import {
  setBills,
  setPackages,
  setSelectedUpgradeMethod,
} from '../payments/payments.actions';
import routes from '../../routes';
import {
  // getCookie,
  setCookie,
  changePopupMsgStatusByLogout,
  changePopupMsgWithPaginationStatusByLogout,
} from '../../utils/persistor';
import {
  clearLocalStorageAfterLogout,
  getNotifMsg,
  handleErrors,
} from '../../utils';
import {
  resetWebsitesState,
  setWhatsappBarcode,
} from '../websites/websites.actions';
import {
  setOperatorReports,
  setOperators,
  setWeeklyChosenOperator,
} from '../operators/operators.actions';
import { setBlacklist } from '../blacklist/blacklist.actions';
import io from 'socket.io-client';
import { SOCKET_CONNECT_URL, POPUP_NAMES } from '../../utils/constants';
import { eventChannel } from 'redux-saga';

const mapState = ({ appData }) => ({
  locale: appData.locale,
});

export function* sendVerificationSms(mobile) {
  const { locale } = yield select(mapState);

  try {
    // TODO: // current error message is so general, better to refactor
    const isSendSms = yield handleSendVerificationSms(mobile);

    if (isSendSms) {
      toast.success(getNotifMsg('FOUR_DIGIT_CODE_SENT_BY_SMS', locale), {
        position: 'bottom-center',
      });

      return true;
    } else {
      toast.info(getNotifMsg('PROBLEM_SENDING_CODE_BY_SMS', locale), {
        position: 'bottom-center',
      });

      return false;
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* verifyUserCredentials({ payload }) {
  const { email, mobile, setIsEmailValid } = payload;
  const { locale } = yield select(mapState);

  try {
    const isEmailExisted = yield isEmailExists(email);
    if (isEmailExisted) {
      toast.error(getNotifMsg('EMAIL_ALREADY_USED', locale), {
        position: 'bottom-center',
      });
      return;
    }
    const isMobileExisted = yield isMobileExists(mobile);
    if (isMobileExisted) {
      toast.error(getNotifMsg('PHONE_NUM_ALREADY_USED', locale), {
        position: 'bottom-center',
      });
      return;
    }
    const isSendSms = yield sendVerificationSms(mobile);
    if (!isSendSms) {
      return;
    }
    const unregisteredUser = yield saveAsUnregisteredUser(payload);
    if (!unregisteredUser.success) {
      toast.error(getNotifMsg('PROBLEM_WHILE_SIGNING_UP', locale), {
        position: 'bottom-center',
      });
      return;
    }
    yield put(setAsUnregisteredUser({ ...payload, id: unregisteredUser.id }));
    yield put(setSignupWizardActivePageName('verifications'));
    yield put(setVerificationCodeTimerStatus(true));
  } catch (error) {
    setIsEmailValid(error?.response?.data?.error?.[0]?.message);
    // handleErrors(error, locale);
  }
}

export function* onVerifyUserCredentialsStart() {
  yield takeLatest(
    userTypes.VERIFY_USER_CREDENTIALS_START,
    verifyUserCredentials
  );
}

export function* validateSmsVerificationCode({ payload }) {
  const { locale } = yield select(mapState);
  const { mobile, verificationCode, setError, onSuccess } = payload;

  try {
    const response = yield call(handleValidateSmsVerificationCode, {
      mobile,
      verificationCode,
    });

    if (response.success) {
      onSuccess && onSuccess(response.data.otpToken);
      yield put(setVerificationCodeTimerStatus(false));

      return;
    }

    return true;
  } catch (error) {
    setError && setError(error?.response?.data?.status);
    handleErrors(error, locale);
  }
}

export function* onValidateSmsVerificationCodeStart() {
  yield takeLatest(
    userTypes.VALIDATE_SMS_VERIFICATION_CODE_START,
    validateSmsVerificationCode
  );
}

export function* resendVerificationSms({ payload }) {
  const isSentSms = yield sendVerificationSms(payload);
  if (isSentSms) {
    yield put(setVerificationCodeTimerStatus(true));
  }
}

export function* onResendVerificationSmsStart() {
  yield takeLatest(
    userTypes.RESEND_VERIFICATION_SMS_START,
    resendVerificationSms
  );
}

export function* loginUser({ payload }) {
  const { userCredentials, history } = payload;
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleLoginUser(userCredentials);
    if (returnedData.id) {
      if (process.env.NODE_ENV === 'development') {
        setCookie('isLoggedIn', 'true', 30);
      }
      setCookie('accessLevel', 'admin', 30);
      localStorage.setItem('isCreatorAdmin', returnedData.isCreator);
      yield put(setCurrentUser(createUserObject(returnedData)));
      yield call(connect, returnedData.token);
      history.push(routes.websites);
    } else if (returnedData.message === 'Invalid Password') {
      toast.error(getNotifMsg('PASSWORD_IS_NOT_CORRECT', locale), {
        position: 'bottom-center',
      });
    } else if (!returnedData.login) {
      toast.error(getNotifMsg('DONT_HAVE_ACCESS_TO_THIS_SECTION', locale), {
        position: 'bottom-center',
      });
    } else if (!returnedData.access) {
      toast.error(getNotifMsg('DONT_HAVE_ACCESS_ADMIN_PANEL', locale), {
        position: 'bottom-center',
      });
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onLoginUserStart() {
  yield takeLatest(userTypes.LOGIN_USER_START, loginUser);
}

export function* logoutUser({ payload: { history } }) {
  const { locale } = yield select(mapState);
  try {
    const isLoggedOut = yield handleLogoutUser();
    if (isLoggedOut) {
      // handling popup status
      changePopupMsgStatusByLogout('SITE_EXCEED_WARNING');
      changePopupMsgWithPaginationStatusByLogout(POPUP_NAMES.NEW_FEATURES);

      clearLocalStorageAfterLogout();
      if (process.env.NODE_ENV === 'development') {
        setCookie('isLoggedIn', 'false', 30);
      }
      yield put(resetStoreDataStart());
      history.push(routes.login);
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onLogoutUserStart() {
  yield takeLatest(userTypes.LOGOUT_USER_START, logoutUser);
}

export function* resetPassword({ payload }) {
  const { values, history } = payload;
  const { locale } = yield select(mapState);

  try {
    const isPasswordReseted = yield handleResetPassword(values);
    if (isPasswordReseted) {
      history.push(routes.login);
      toast.success(getNotifMsg('RESET_PASS_EMAIL_SENT', locale), {
        position: 'bottom-center',
      });
    }
  } catch (error) {
    if (error.response && error.response.status === 403) {
      toast.error(getNotifMsg('NO_ACCOUNT_WITH_THIS_EMAIL', locale), {
        position: 'bottom-center',
      });
      return;
    }
    handleErrors(error, locale);
  }
}

export function* onResetPasswordStart() {
  yield takeLatest(userTypes.RESET_PASSWORD_START, resetPassword);
}

export function* signupUser({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const signupData = yield handleSignup(payload);

    if (!signupData.success) {
      toast.error(getNotifMsg('PROBLEM_WHILE_SIGNING_UP', locale), {
        position: 'bottom-center',
      });
      return;
    }
    yield put(setWidgetCode(signupData.data.channel.code));
    yield put(setWidgetToken(signupData.data.channel.token));
    yield put(setSignupWizardActivePageName('dashboard-landing'));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onSignupStart() {
  yield takeLatest(userTypes.SIGNUP_START, signupUser);
}

export function* autoLoginUser(action) {
  const { locale } = yield select(mapState);

  try {
    yield loginUser(action);
    yield put(setSignupWizardActivePageName('signup'));
    yield put(setAsUnregisteredUser({}));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onAutoLoginUserStart() {
  yield takeLatest(userTypes.AUTO_LOGIN_USER_START, autoLoginUser);
}

export function* validateSmsAndRedirectToPayment({ payload }) {
  // const { invoiceId } = payload;
  const { locale } = yield select(mapState);

  try {
    const isValidCode = yield handleValidateSmsVerificationCode(payload);
    if (!isValidCode) {
      toast.error(getNotifMsg('ENTERED_CODE_WRONG_TRY_AGAIN', locale), {
        position: 'bottom-center',
      });
      return;
    }
    yield put(setVerificationCodeTimerStatus(false));
    if (payload.isPackageUpgrade) {
      // yield put(setIsAutoUpgradeVisible(true));
      yield put(setSelectedUpgradeMethod('manual'));
      // yield put(setIsMobileValidated(true));
    } else {
      // handleRedirectToPayment(invoiceId);
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onValidateSmsAndRedirectToPaymentStart() {
  yield takeLatest(
    userTypes.VALIDATE_SMS_AND_REDIRECT_TO_PAYMENT_START,
    validateSmsAndRedirectToPayment
  );
}

export function* resetStoreData() {
  yield put(resetWebsitesState());
  yield put(setOperators(null));
  yield put(setWeeklyChosenOperator({}));
  yield put(setPackages(null));
  yield put(setBills(null));
  yield put(setBlacklist(null));
  yield put(setOperatorReports(null));
  yield put(setCurrentUser(INITIAL_STATE.profile));
}

export function* onResetStoreData() {
  yield takeLatest(userTypes.RESET_STORE_DATA, resetStoreData);
}

function connect(token) {
  const socket = io(SOCKET_CONNECT_URL, {
    reconnectionDelay: 1000,
    reconnection: true,
    reconnectionAttemps: 10,
    transports: ['websocket'],
    agent: false,
    upgrade: false,
    rejectUnauthorized: false,
    query: {
      type: 'agent',
      token,
    },
    path: '/socket.io',
  });

  return new Promise((resolve) => {
    socket.on('connect', () => {
      resolve(socket);
    });
  });
}

function subscribe(socket) {
  return eventChannel((emit) => {
    socket.on('whatsapp-qr-code', (resData) => {
      emit(setWhatsappBarcode(resData.qr));
    });

    return () => {};
  });
}

function* read(socket) {
  const channel = yield call(subscribe, socket);
  while (true) {
    let action = yield take(channel);
    yield put(action);
  }
}

function* handleIO(socket) {
  yield fork(read, socket);
}

// at the moment it has no use. so temporary disabled.
export function* flow() {
  const token = localStorage.getItem('token');
  // const login = getCookie('isLoggedIn');
  if (token) {
    const socket = yield call(connect, token);
    yield fork(handleIO, socket);
  }
}

export function* postUserDataForOTVSaga(action) {
  const { locale } = yield select(mapState);

  try {
    const response = yield call(handleUserVerifyWithOTV, action.payload);
    if (response.success) {
      yield put(postUserDataForOTVSuccess(response));
      if (action.payload.type === 'email') {
        toast.success(getNotifMsg('SEND_CODE_REQUEST_EMAIL', locale));
        return;
      }
      toast.success(getNotifMsg('SEND_CODE_REQUEST_PHONE', locale));
      return;
    }
  } catch (error) {
    yield put(postUserDataForOTVFailure(error));
    handleErrors(error, locale);
  }
}

export function* onPostUserDataOTV() {
  yield takeLatest(userTypes.POST_USER_DATA_OF_OTV, postUserDataForOTVSaga);
}

function* onSendVerificationSms({ payload }) {
  const { mobile, onSuccess } = payload;
  const isSent = yield call(sendVerificationSms, mobile);

  if (isSent) onSuccess && onSuccess();
}

function* watchSendVerificationSms() {
  yield takeLatest(userTypes.SEND_VERIFICATION_SMS, onSendVerificationSms);
}

export function* loginUserWithOneTimeCode({ payload }) {
  const { userCredentials, history } = payload;
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleUserLoginWithOTV(userCredentials);
    if (returnedData.id) {
      if (process.env.NODE_ENV === 'development') {
        setCookie('isLoggedIn', 'true', 30);
      }
      setCookie('accessLevel', 'admin', 30);
      localStorage.setItem('isCreatorAdmin', returnedData.isCreator);
      yield put(setCurrentUser(createUserObject(returnedData)));
      yield call(connect, returnedData.token);
      history.push(routes.websites);
    } else if (!returnedData.login) {
      toast.error(getNotifMsg('DONT_HAVE_ACCESS_TO_THIS_SECTION', locale), {
        position: 'bottom-center',
      });
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onLoginUserWithOneTimeCodeStart() {
  yield takeLatest(
    userTypes.LOGIN_USER_ONE_TIME_CODE,
    loginUserWithOneTimeCode
  );
}

export default function* userSagas() {
  yield all([
    call(onVerifyUserCredentialsStart),
    call(onValidateSmsVerificationCodeStart),
    call(onResendVerificationSmsStart),
    call(onLoginUserStart),
    call(onLogoutUserStart),
    call(onResetPasswordStart),
    call(onSignupStart),
    call(onAutoLoginUserStart),
    call(onValidateSmsAndRedirectToPaymentStart),
    call(onResetStoreData),
    call(flow),
    call(onPostUserDataOTV),
    call(onLoginUserWithOneTimeCodeStart),
    call(watchSendVerificationSms),
  ]);
}
